// src/components/CircuitBackground.js

import React, { useState, useLayoutEffect, useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ReactComponent as CircuitSvg } from "../assets/circuit.svg";
import "./CircuitBackground.css";

const HOME_SPOT_RADIUS = "520px";
const SHELTERED_SPOT_RADIUS = "380px";

// Helper component - no changes needed here
const CircuitTiles = ({ tileCount }) => (
  <>
    {Array.from({ length: tileCount }).map((_, index) => {
      const transform = `scaleX(${index % 2 === 1 ? -1 : 1})`;
      return (
        <CircuitSvg
          key={index}
          className="circuit-svg-tile"
          style={{ transform }}
        />
      );
    })}
  </>
);

const CircuitBackground = () => {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [tileCount, setTileCount] = useState(1);

  const animationFrameId = useRef(null); // Ref to control the animation loop
  const writeRafId = useRef(null);
  const latestRef = useRef({ x: 0, y: 0, sheltered: false });

  // NEW: Ref to store the spotlight's current position.
  // This allows the animation to know where it is at all times.
  const spotlightPosition = useRef({
    x: typeof window !== "undefined" ? window.innerWidth / 2 : 0,
    y: typeof window !== "undefined" ? window.innerHeight / 2 : 0,
  });

  // NEW: State to track if the user is actively touching the screen
  const [isInteracting, setIsInteracting] = useState(false);

  // Tiling and mouse-tracking logic is perfect, no changes needed
  useLayoutEffect(() => {
    const calculateTiles = () => {
      if (svgRef.current) {
        const screenWidth = window.innerWidth;
        const svgWidth = svgRef.current.clientWidth;
        if (svgWidth > 0) {
          const count = Math.ceil(screenWidth / svgWidth) + 1;
          setTileCount(count);
        }
      }
    };
    calculateTiles();
    window.addEventListener("resize", calculateTiles);
    return () => window.removeEventListener("resize", calculateTiles);
  }, []);

  // --- HOOK 1: EVENT HANDLERS ---
  // This hook is now responsible for setting up and tearing down ALL event listeners.
  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const renderFromLatest = () => {
      writeRafId.current = null;
      const { x, y } = latestRef.current;
      spotlightPosition.current = { x, y };
      const el = containerRef.current;
      if (!el) return;
      // Only update position — highlight size/brightness is controlled
      // exclusively by the route change hook to avoid race conditions
      // with AnimatePresence exit animations.
      el.style.setProperty("--mouse-x", `${x}px`);
      el.style.setProperty("--mouse-y", `${y}px`);
    };
    const updatePosition = (x, y) => {
      latestRef.current.x = x;
      latestRef.current.y = y;
      if (!writeRafId.current) {
        writeRafId.current = requestAnimationFrame(renderFromLatest);
      }
    };

    if (isTouchDevice) {
      const passive = { passive: true };
      const handleTouchStart = (e) => {
        setIsInteracting(true);
        updatePosition(e.touches[0].clientX, e.touches[0].clientY);
      };
      const handleTouchMove = (e) => {
        updatePosition(e.touches[0].clientX, e.touches[0].clientY);
      };
      const handleTouchEnd = () => setIsInteracting(false);

      window.addEventListener("touchstart", handleTouchStart, passive);
      window.addEventListener("touchmove", handleTouchMove, passive);
      window.addEventListener("touchend", handleTouchEnd, passive);
      window.addEventListener("touchcancel", handleTouchEnd, passive);
      return () => {
        window.removeEventListener("touchstart", handleTouchStart, passive);
        window.removeEventListener("touchmove", handleTouchMove, passive);
        window.removeEventListener("touchend", handleTouchEnd, passive);
        window.removeEventListener("touchcancel", handleTouchEnd, passive);
      };
    } else {
      const handleMouseMove = (e) =>
        updatePosition(e.clientX, e.clientY);
      window.addEventListener("mousemove", handleMouseMove);
      // return () => window.removeEventListener("mousemove", handleMouseMove);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        if (writeRafId.current) cancelAnimationFrame(writeRafId.current);
        writeRafId.current = null;
      };
    }
  }, []);

  // --- HOOK: RE-EVALUATE ON ROUTE CHANGE ---
  // Home page (/) has no shelter (big bright highlight).
  // All other pages are sheltered (subtle highlight).
  // We use the pathname directly because AnimatePresence mode="wait"
  // keeps the old page in the DOM during exit, so elementFromPoint
  // would hit stale DOM elements.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const sheltered = location.pathname !== "/";
    latestRef.current.sheltered = sheltered;
    el.style.setProperty("--highlight-opacity", sheltered ? "0.15" : "0.8");
    el.style.setProperty(
      "--spot-radius",
      sheltered ? SHELTERED_SPOT_RADIUS : HOME_SPOT_RADIUS
    );
  }, [location.pathname]);

  // --- HOOK 2: ANIMATION CONTROLLER ---
  // This hook is ONLY responsible for the ambient animation loop.
  // It depends on `isInteracting` to know when to run.
  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (!(isTouchDevice && !isInteracting)) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      return;
    }

    const animate = (time) => {
      const speed = 2000;
      const targetX =
        window.innerWidth / 2 +
        Math.cos(time / speed) * (window.innerWidth / 2.5);
      const targetY =
        window.innerHeight / 2 +
        Math.sin((2 * time) / speed) * (window.innerHeight / 3);

      const currentX = spotlightPosition.current.x;
      const currentY = spotlightPosition.current.y;

      const easingFactor = 0.05;
      const newX = currentX + (targetX - currentX) * easingFactor;
      const newY = currentY + (targetY - currentY) * easingFactor;

      spotlightPosition.current = { x: newX, y: newY };
      if (containerRef.current) {
        containerRef.current.style.setProperty("--mouse-x", `${newX}px`);
        containerRef.current.style.setProperty("--mouse-y", `${newY}px`);
      }

      animationFrameId.current = requestAnimationFrame(animate);
    };

    const startLoop = () => {
      if (!document.hidden && !animationFrameId.current) {
        animationFrameId.current = requestAnimationFrame(animate);
      }
    };

    const stopLoop = () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    };

    startLoop();

    const handleVisibility = () => {
      if (document.hidden) {
        stopLoop();
      } else {
        startLoop();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopLoop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isInteracting]); // This effect re-runs whenever `isInteracting` changes.

  // --- THE ANIMATION CHANGE ---
  // The magic happens here in the motion.div props.
  return (
    <motion.div
      ref={containerRef}
      className="circuit-background-container"
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      animate={prefersReducedMotion ? undefined : { opacity: 1 }}
      transition={
        prefersReducedMotion
          ? undefined
          : {
              delay: 0.18,
              duration: 0.42,
              ease: "easeOut",
            }
      }
    >
      <CircuitSvg ref={svgRef} className="svg-measuring-stick" />

      {/* The rest of your component remains the same */}
      <div className="circuit-layer base-layer">
        <CircuitTiles tileCount={tileCount} />
      </div>
      <div className="circuit-layer highlight-layer">
        <CircuitTiles tileCount={tileCount} />
      </div>

      {/* <div className="glass-pane" /> */}
    </motion.div>
  );
};

export default CircuitBackground;
